import { App, reactive } from "vue";

export default {
	install: function (app: App) {
		// 1. Reactive state for detailed breakpoints, macro views, and orientation
		const screen_state = reactive({
			// Micro breakpoints
			is_extra_small: false,
			is_small: false,
			is_medium: false,
			is_large: false,
			is_extra_large: false,
			// Macro categories
			is_mobile: false,
			is_tablet: false,
			is_desktop: false,
			// Orientation
			is_portrait: false,
			is_landscape: false
		});

		if (typeof window !== "undefined") {
			// 2. Define native CSS media queries
			const queries = {
				xs: window.matchMedia("(max-width: 580px)"),
				sm: window.matchMedia("(min-width: 581px) and (max-width: 767px)"),
				md: window.matchMedia("(min-width: 768px) and (max-width: 991px)"),
				lg: window.matchMedia("(min-width: 992px) and (max-width: 1199px)"),
				xl: window.matchMedia("(min-width: 1200px)"),
				// Macro queries
				mobile: window.matchMedia("(max-width: 767px)"),
				tablet: window.matchMedia("(min-width: 768px) and (max-width: 991px)"),
				desktop: window.matchMedia("(min-width: 992px)"),
				// Orientation queries
				portrait: window.matchMedia("(orientation: portrait)"),
				landscape: window.matchMedia("(orientation: landscape)")
			};

			// Function to sync current query matches with the reactive state
			const update_state = () => {
				screen_state.is_extra_small = queries.xs.matches;
				screen_state.is_small = queries.sm.matches;
				screen_state.is_medium = queries.md.matches;
				screen_state.is_large = queries.lg.matches;
				screen_state.is_extra_large = queries.xl.matches;

				screen_state.is_mobile = queries.mobile.matches;
				screen_state.is_tablet = queries.tablet.matches;
				screen_state.is_desktop = queries.desktop.matches;

				screen_state.is_portrait = queries.portrait.matches;
				screen_state.is_landscape = queries.landscape.matches;
			};

			// Initial evaluation
			update_state();

			// 3. Attach listeners
			Object.values(queries).forEach((query) => {
				query.addEventListener("change", update_state);
			});
		}

		const globals = app.config.globalProperties;

		// 4. Granular helpers
		globals.$is_extra_small_device = (): boolean => screen_state.is_extra_small;
		globals.$is_small_device = (): boolean => screen_state.is_small;
		globals.$is_medium_device = (): boolean => screen_state.is_medium;
		globals.$is_large_device = (): boolean => screen_state.is_large;
		globals.$is_extra_large_device = (): boolean => screen_state.is_extra_large;
		globals.$is_big_device = (): boolean =>
			screen_state.is_large || screen_state.is_extra_large;

		// 5. Macro helpers
		globals.$is_mobile = (): boolean => screen_state.is_mobile;
		globals.$is_tablet = (): boolean => screen_state.is_tablet;
		globals.$is_desktop = (): boolean => screen_state.is_desktop;
		globals.$is_mobile_or_tablet = (): boolean =>
			screen_state.is_mobile || screen_state.is_tablet;

		// 6. Orientation helpers
		globals.$is_portrait = (): boolean => screen_state.is_portrait;
		globals.$is_landscape = (): boolean => screen_state.is_landscape;
	}
};

declare module "@vue/runtime-core" {
	interface ComponentCustomProperties {
		$is_extra_small_device: () => boolean;
		$is_small_device: () => boolean;
		$is_medium_device: () => boolean;
		$is_large_device: () => boolean;
		$is_extra_large_device: () => boolean;
		$is_big_device: () => boolean;
		$is_mobile: () => boolean;
		$is_tablet: () => boolean;
		$is_desktop: () => boolean;
		$is_mobile_or_tablet: () => boolean;
		$is_portrait: () => boolean;
		$is_landscape: () => boolean;
	}
}
